import React, { Component, Children } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import styles from './checkbox.module.css'

/**
 * Get index position of element within given array.
 *
 * @private
 * @param {Array.<string>} list - The array for lookup
 * @param {string} value - The value to be searched
 * @returns {number} The current element index or -1
 */
const getValueIndexFromList = (list, value) => {
    if (!list || !list.length) {
        return -1
    }
    return list.indexOf(value)
}

/**
 * Check if value is in array.
 *
 * @private
 * @param {Array.<string>} list - The array for lookup
 * @param {string} value - The value to be searched
 * @returns {boolean} Whether the element is in the list or not
 */
const isValueInList = (list, value) => {
    return !!~getValueIndexFromList(list, value)
}

/**
 * Add single value to array.
 *
 * @private
 * @param {Array.<string>} list - The target array
 * @param {string} value - The value to be inserted
 * @returns {Array.<string>} The new created array
 */
const addValueToList = (list, value) => {
    return [...list, value]
}

/**
 * Remove single value to array.
 *
 * @private
 * @param {Array.<string>} list - The target array
 * @param {string} value - The value to be removed
 * @returns {Array.<string>} The new created array
 */
const removeValueFromList = (list, value) => {
    const index = getValueIndexFromList(list, value)
    return [...list.slice(0, index), ...list.slice(index + 1)]
}

/**
 * Check for deprecated component props and show a console warning. This function will also
 * merge deprecated props into the new according props.
 *
 * @private
 * @param {Object} props - The component props to be checked
 * @returns {Object} The checked and filtered props
 */
const checkForDeprecatedProps = props => {
    let {
        disabledValue, // eslint-disable-line prefer-const
        disabledValues,
        onUpdate, // eslint-disable-line prefer-const
        selectedValue, // eslint-disable-line prefer-const
        selectedValues, // eslint-disable-line prefer-const
        ...otherProps // eslint-disable-line prefer-const
    } = props

    if (selectedValue) {
        console.warn(
            `Warning CheckboxGroup: Using selectedValue string
            property is deprecated, use selectedValues
            with an array of values instead.`
        )
        selectedValues = addValueToList(selectedValues, selectedValue)
    }

    if (disabledValue) {
        console.warn(
            `Warning CheckboxGroup: Using disabledValue string
            property is deprecated, use disabledValues
            with an array of values instead.`
        )
        disabledValues = addValueToList(disabledValues, disabledValue)
    }

    if (onUpdate && onUpdate !== Function.prototype) {
        console.warn(
            `Warning CheckboxGroup: Using onUpdate
            property is deprecated, use onChange instead.`
        )
    }

    return {
        disabledValues,
        onUpdate,
        selectedValues,
        ...otherProps
    }
}

/**
 * Validate props via React.PropTypes helpers. See class description for type information.
 *
 * @static
 * @type {React.PropTypes}
 */
const propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    disabledValue: PropTypes.string,
    disabledValues: PropTypes.arrayOf(PropTypes.string),
    error: PropTypes.instanceOf(Error),
    onChange: PropTypes.func,
    onUpdate: PropTypes.func,
    selectedValue: PropTypes.string,
    selectedValues: PropTypes.arrayOf(PropTypes.string),
    style: PropTypes.object // eslint-disable-line react/forbid-prop-types
}

/**
 * Define default props as fallback. See class description for type information.
 *
 * @static
 * @type {React.DefaultProps}
 */
const defaultProps = {
    disabledValues: [],
    onChange: Function.prototype,
    onUpdate: Function.prototype,
    selectedValues: []
}

/**
 * Class representing a checkbox group containing multiple single checkbox elements as children.
 *
 * @class
 * @extends FormField
 *
 * @example <caption>Basic usage</caption>
 * <code>
 * <CheckboxGroup selectedValues={['a', 'b']} disabledValues={['c']}>
 *      <Checkbox label='Value A' value='a' name='example' />
 *      <Checkbox label='Value B' value='b' name='example' />
 *      <Checkbox label='Value C' value='c' name='example' />
 *      <Checkbox label='Value D' value='d' name='example' />
 *      <Checkbox label='Value E' value='e' name='example' />
 * </CheckboxGroup>
 * </code>
 *
 * @property {Array.<string>|string} props.children - The Checkbox children to be rendered
 * @property {string} [props.className] - Define additional className for wrapper fieldset
 * @property {string} [props.disabledValue] - DEPRECATED! Define disabled checkbox by value
 * @property {Array.<string>} [props.disabledValues=[]] - Define disabled checkboxes by array of values
 * @property {Function} [props.error] - The error popover handler
 * @property {Function} [props.onChange=noop] - Define callback handler when single checkbox changed
 * @property {Function} [props.onUpdate=noop] - DEPRECATED! Define callback handler when single checkbox changed
 * @property {string} [props.selectedValue] - DEPRECATED! Define selected checkbox by value
 * @property {Array.<string>} [props.selectedValues=[]] - Define selected checkboxes by array of values
 * @property {Object} [props.style] - Define additional styles for wrapper fieldset
 */
class CheckboxGroup extends Component {
    /**
     * The actual class constructor.
     *
     * @constructs
     * @param {Object} props - The component props
     * @returns {void}
     */
    constructor(props) {
        super(props)

        props = checkForDeprecatedProps(props)

        this.onChange = this.onChange.bind(this)

        this.state = {
            disabledValues: props.disabledValues,
            selectedValues: props.selectedValues
        }
    }

    /**
     * Setting the state after new props recieved
     *
     * @param {Object} nextProps - The new component props
     * @returns {void}
     */
    UNSAFE_componentWillReceiveProps(nextProps) {
        nextProps = checkForDeprecatedProps(nextProps)

        this.setState({
            disabledValues: nextProps.disabledValues,
            selectedValues: nextProps.selectedValues
        })
    }

    /**
     * The required render function to return react markup
     *
     * @returns {ReactElement} React component markup
     */
    render() {
        const { children, className, style } = this.props
        const renderCheckboxChildren = this.renderCheckboxChildren(children)

        return (
            <fieldset className={cx(styles['checkbox-group'], className)} style={style}>
                {renderCheckboxChildren}
            </fieldset>
        )
    }

    /**
     * Get the current selected values as array
     *
     * @returns {Array.<string>} The selected values
     */
    getValue() {
        return this.state.selectedValues
    }

    /**
     * Handle checkbox change event
     *
     * @param {Object} event - The event object
     * @returns {void}
     */
    onChange(event) {
        const value = event.target && event.target.value
        const { onChange, onUpdate } = this.props

        let selectedValues = this.state.selectedValues
        if (this.isChecked(value)) {
            selectedValues = removeValueFromList(selectedValues, value)
        } else {
            selectedValues = addValueToList(selectedValues, value)
        }

        this.setState(
            {
                selectedValues: selectedValues
            },
            () => {
                onChange(value)
                onUpdate(value)
            }
        )
    }

    /**
     * Check if value is selected
     *
     * @param {string} value - The value to be checked
     * @returns {boolean} Wether the value is selected or not
     */
    isChecked(value) {
        return isValueInList(this.state.selectedValues, value)
    }

    /**
     * Check if value is disabled
     *
     * @param {string} value - The value to be checked
     * @returns {boolean} Wether the value is disabled or not
     */
    isDisabled(value) {
        return isValueInList(this.state.disabledValues, value)
    }

    /**
     * Render props children and assign new properties to each child
     *
     * @throws {Error} Will throw when there are no checkbox childs
     * @param {Array|string} children - The component children
     * @returns {ReactElement} Wether the value is disabled or not
     */
    renderCheckboxChildren(children) {
        const childrenCount = Children.count(children)

        if (childrenCount < 1) {
            throw new Error('CheckboxGroup: A Checkbox group needs to have at least one Checkbox!')
        }

        return Children.map(children, child => {
            return React.cloneElement(child, {
                checked: this.isChecked(child.props.value),
                disabled: this.isDisabled(child.props.value),
                onChange: this.onChange
            })
        })
    }
}

CheckboxGroup.propTypes = propTypes
CheckboxGroup.defaultProps = defaultProps

export { CheckboxGroup }
